import { useState, useRef, useMemo, useEffect } from "react";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { rowNavHandlers } from "@/lib/row-nav";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner } from "@/hooks/use-permissions";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, LayoutList, Columns, TrendingUp, DollarSign, Clock, Home, ArrowUpDown, ArrowUp, ArrowDown, ClipboardList, MapPin, Upload, FileText, Sparkles, CheckCircle2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ContractExtractionProgress } from "@/components/contract-extraction-progress";
import { format, differenceInDays } from "date-fns";
import type { Acquisition, AcquisitionStatus, Property } from "@shared/schema";
import { ACQUISITION_STATUSES } from "@shared/schema";
import AddressAutocomplete from "@/components/address-autocomplete";
import { JourneyEmptyState, useJourneyStage } from "@/lib/journey-empty-states";
import { usePageTitle } from "@/hooks/use-page-title";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";

const ACQUISITION_SORT_FIELDS = ["address", "status", "offerPrice", "deadline", "daysInStatus"] as const;
type AcquisitionSortField = (typeof ACQUISITION_SORT_FIELDS)[number];

const STATUS_LABELS: Record<AcquisitionStatus, string> = {
  prospect: "Prospect",
  offer_submitted: "Offer Submitted",
  offer_accepted: "Offer Accepted",
  under_contract: "Under Contract",
  due_diligence: "Due Diligence",
  appraisal: "Appraisal",
  clear_to_close: "Clear to Close",
  closing: "Closing",
  closed: "Closed",
};

const STATUS_COLORS: Record<AcquisitionStatus, string> = {
  prospect: "bg-gray-100 text-gray-700",
  offer_submitted: "bg-blue-100 text-blue-700",
  offer_accepted: "bg-indigo-100 text-indigo-700",
  under_contract: "bg-purple-100 text-purple-700",
  due_diligence: "bg-yellow-100 text-yellow-700",
  appraisal: "bg-orange-100 text-orange-700",
  clear_to_close: "bg-teal-100 text-teal-700",
  closing: "bg-emerald-100 text-emerald-700",
  closed: "bg-green-100 text-green-700",
};

function formatCurrency(val: string | null | undefined): string {
  if (!val) return "—";
  const n = parseFloat(val.replace(/[^0-9.]/g, ""));
  if (isNaN(n)) return val;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function getNextDeadline(acq: Acquisition): { label: string; date: string } | null {
  const today = new Date();
  const candidates: { label: string; date: string }[] = [
    { label: "Offer Exp.", date: acq.offerExpirationDate || "" },
    { label: "Inspection", date: acq.inspectionDeadline || "" },
    { label: "Appraisal", date: acq.appraisalDeadline || "" },
    { label: "Fin. Contingency", date: acq.financingContingencyDeadline || "" },
    { label: "Closing", date: acq.closingDateScheduled || "" },
  ].filter((c) => c.date && new Date(c.date) >= today);
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  return candidates[0];
}

function getDaysInStatus(acq: Acquisition): number {
  const changedAt = acq.statusChangedAt ? new Date(acq.statusChangedAt) : new Date(acq.createdAt);
  return differenceInDays(new Date(), changedAt);
}

function formatPhone(val: string): string {
  const d = val.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function PhoneInput({ value, onChange, testId }: { value: string; onChange: (v: string) => void; testId?: string }) {
  return (
    <Input
      type="tel"
      value={formatPhone(value)}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 10))}
      className="text-sm h-8"
      data-testid={testId}
    />
  );
}

const CONTRACT_FIELD_LABELS: Record<string, string> = {
  offerPrice: "Offer / Purchase Price",
  earnestMoney: "Earnest Money",
  optionFee: "Option Fee",
  offerDate: "Offer Date",
  contractDate: "Contract Date",
  optionPeriodExpiration: "Option Period Expiration",
  inspectionDeadline: "Inspection Deadline",
  appraisalDeadline: "Appraisal Deadline",
  closingDateScheduled: "Closing Date (Scheduled)",
  agentName: "Buyer Agent Name",
  agentPhone: "Buyer Agent Phone",
  agentEmail: "Buyer Agent Email",
  titleCompanyName: "Title Company Name",
  sellerName: "Seller Name",
};
const CONTRACT_FIELD_KEYS = Object.keys(CONTRACT_FIELD_LABELS);
const DATE_FIELDS_SET = new Set(["offerDate", "contractDate", "optionPeriodExpiration", "inspectionDeadline", "appraisalDeadline", "closingDateScheduled"]);
const PHONE_FIELDS_SET = new Set(["agentPhone"]);
const CURRENCY_FIELDS_SET = new Set(["offerPrice", "earnestMoney", "optionFee"]);
const ADDRESS_FIELDS_SET = new Set(["propertyAddress", "propertyCity", "propertyState", "propertyZipCode"]);
const ADDRESS_LABELS: Record<string, string> = {
  propertyAddress: "Street Address",
  propertyCity: "City",
  propertyState: "State",
  propertyZipCode: "ZIP Code",
};

function CreateAcquisitionDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [step, setStep] = useState<"source" | "pick-property" | "new-address" | "details" | "upload-contract" | "review-contract">("source");
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [address, setAddress] = useState("");
  const [status, setStatus] = useState<AcquisitionStatus>("prospect");
  const [offerPrice, setOfferPrice] = useState("");
  const [isFromUnderwriting, setIsFromUnderwriting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const extractAbortRef = useRef<AbortController | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState<Record<string, string | null> | null>(null);
  const [pdfDataUrl, setPdfDataUrl] = useState("");
  const [pdfFileName, setPdfFileName] = useState("");
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});

  const { data: properties = [] } = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const underwritingProperties = properties.filter((p) => p.status === "underwriting");

  const resetAndClose = () => {
    if (extractAbortRef.current) {
      extractAbortRef.current.abort();
      extractAbortRef.current = null;
    }
    setStep("source");
    setSelectedPropertyId(null);
    setAddress("");
    setStatus("prospect");
    setOfferPrice("");
    setIsFromUnderwriting(false);
    setExtracted(null);
    setPdfDataUrl("");
    setPdfFileName("");
    setAccepted({});
    setEditedValues({});
    setExtracting(false);
    onClose();
  };

  const handleContractFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isPdf = file.type === "application/pdf" || String(file.name ?? "").toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast({ title: "Only PDF files are supported", variant: "destructive" });
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast({ title: "File too large (max 15MB)", variant: "destructive" });
      return;
    }

    setExtracting(true);
    const controller = new AbortController();
    extractAbortRef.current = controller;
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const base64 = dataUrl.split(",")[1];
      const res = await apiRequest(
        "POST",
        "/api/app/acquisitions/extract-contract-standalone",
        { pdfBase64: base64 },
        { signal: controller.signal },
      );
      const data = await res.json();

      if (controller.signal.aborted) return;

      if (data.extracted) {
        setExtracted(data.extracted);
        setPdfDataUrl(dataUrl);
        setPdfFileName(file.name);
        const initAccepted: Record<string, boolean> = {};
        const initEdited: Record<string, string> = {};
        [...CONTRACT_FIELD_KEYS, ...Object.keys(ADDRESS_LABELS)].forEach((k) => {
          const val = data.extracted[k];
          initAccepted[k] = val != null && val !== "";
          initEdited[k] = val || "";
        });
        setAccepted(initAccepted);
        setEditedValues(initEdited);
        setStep("review-contract");
      } else {
        toast({ title: data.message || "Extraction failed", variant: "destructive" });
      }
    } catch (err: unknown) {
      if (controller.signal.aborted || (err instanceof Error && err.name === "AbortError")) {
        return;
      }
      console.error("[CreateAcquisitionDialog] contract extraction failed:", err);
      toast({
        title: "AI extraction failed",
        description: parseErrorMessage(err, "Couldn't extract data from this contract. Please try again."),
        variant: "destructive",
      });
    } finally {
      if (extractAbortRef.current === controller) {
        extractAbortRef.current = null;
      }
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const createFromContractMutation = useMutation({
    mutationFn: async () => {
      const fields: Record<string, string | null> = {};
      [...CONTRACT_FIELD_KEYS, ...Object.keys(ADDRESS_LABELS)].forEach((k) => {
        if (accepted[k]) {
          const val = editedValues[k] || null;
          fields[k] = PHONE_FIELDS_SET.has(k) ? (val?.replace(/\D/g, "") || null) : val;
        }
      });
      const res = await apiRequest("POST", "/api/app/acquisitions/create-from-contract", {
        fields,
        pdfFileName,
        pdfDataUrl,
      });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions"] });
      toast({ title: "Acquisition created from contract" });
      resetAndClose();
      if (data?.id) navigate(`/app/acquisitions/${data.id}`);
    },
    onError: () => {
      toast({ title: "Failed to create acquisition from contract", variant: "destructive" });
    },
  });

  const createFromPropertyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/app/acquisitions/from-property/${selectedPropertyId}`, {
        status,
        offerPrice: offerPrice || null,
      });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/properties"] });
      toast({ title: "Acquisition created from underwriting" });
      resetAndClose();
      if (data?.id) navigate(`/app/acquisitions/${data.id}`);
    },
    onError: () => {
      toast({ title: "Failed to create acquisition", variant: "destructive" });
    },
  });

  const createNewMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/acquisitions", {
        address,
        status,
        offerPrice: offerPrice || null,
      });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions"] });
      toast({ title: "Acquisition created" });
      resetAndClose();
      if (data?.id) navigate(`/app/acquisitions/${data.id}`);
    },
    onError: () => {
      toast({ title: "Failed to create acquisition", variant: "destructive" });
    },
  });

  const handleCreate = () => {
    if (isFromUnderwriting && selectedPropertyId) {
      createFromPropertyMutation.mutate();
    } else {
      createNewMutation.mutate();
    }
  };

  const isPending = createFromPropertyMutation.isPending || createNewMutation.isPending;

  const isInFlight = extracting || createFromContractMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isInFlight) return;
        if (!next) resetAndClose();
      }}
    >
      <DialogContent
        className={step === "review-contract" ? "max-w-2xl max-h-[90vh] overflow-y-auto" : "max-w-md"}
        data-testid="dialog-create-acquisition"
        onInteractOutside={(e) => {
          if (isInFlight) {
            e.preventDefault();
            return;
          }
          const target = e.target as HTMLElement | null;
          if (target?.closest?.('[data-address-autocomplete-portal]')) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (isInFlight) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isInFlight) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {step === "source" && "Add Acquisition"}
            {step === "pick-property" && "Select Underwritten Property"}
            {step === "new-address" && "Enter Property Address"}
            {step === "details" && "Acquisition Details"}
            {step === "upload-contract" && "Upload Executed Contract"}
            {step === "review-contract" && "Review Extracted Contract Data"}
          </DialogTitle>
          <DialogDescription className="sr-only">Create a new acquisition to track through your pipeline</DialogDescription>
        </DialogHeader>

        {step === "source" && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">How would you like to add this acquisition?</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => {
                  setIsFromUnderwriting(true);
                  setStep("pick-property");
                }}
                className="flex flex-col items-center gap-2 p-4 border-2 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer"
                data-testid="button-from-underwriting"
              >
                <ClipboardList className="h-8 w-8 text-blue-600" />
                <span className="text-sm font-medium text-center">From Underwriting</span>
                <span className="text-xs text-gray-500 text-center">Select from your underwritten properties</span>
              </button>
              <button
                onClick={() => {
                  setIsFromUnderwriting(false);
                  setStep("new-address");
                }}
                className="flex flex-col items-center gap-2 p-4 border-2 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors cursor-pointer"
                data-testid="button-new-property"
              >
                <MapPin className="h-8 w-8 text-green-600" />
                <span className="text-sm font-medium text-center">New Property</span>
                <span className="text-xs text-gray-500 text-center">Enter a new property address</span>
              </button>
              <button
                onClick={() => setStep("upload-contract")}
                className="flex flex-col items-center gap-2 p-4 border-2 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors cursor-pointer"
                data-testid="button-upload-contract"
              >
                <FileText className="h-8 w-8 text-purple-600" />
                <span className="text-sm font-medium text-center">Upload & Extract Contract</span>
                <span className="text-xs text-gray-500 text-center">Extract details from an executed contract</span>
              </button>
            </div>
          </div>
        )}

        {step === "pick-property" && (
          <div className="space-y-3">
            {underwritingProperties.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm font-medium">No underwriting properties found</p>
                <p className="text-xs mt-1">Add a property to underwriting first, or enter a new address.</p>
                <Button variant="outline" className="mt-3" onClick={() => setStep("new-address")} data-testid="button-switch-to-new">
                  Enter address instead
                </Button>
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto space-y-2">
                {underwritingProperties.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => {
                      setSelectedPropertyId(p.id);
                      setAddress(p.address || p.name);
                      setStep("details");
                    }}
                    className={`w-full text-left p-3 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer ${
                      selectedPropertyId === p.id ? "border-blue-500 bg-blue-50" : ""
                    }`}
                    data-testid={`select-property-${p.id}`}
                  >
                    <p className="text-sm font-medium text-gray-900">{p.name}</p>
                    {p.address && <p className="text-xs text-gray-500 mt-0.5">{p.address}</p>}
                    {p.purchasePrice && <p className="text-xs text-gray-400 mt-0.5">Price: {formatCurrency(p.purchasePrice)}</p>}
                  </button>
                ))}
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={() => setStep("source")} data-testid="button-back-to-source">
              ← Back
            </Button>
          </div>
        )}

        {step === "new-address" && (
          <div className="space-y-4">
            <div>
              <Label>Property Address *</Label>
              <AddressAutocomplete
                value={address}
                onSelect={(addr) => setAddress(addr)}
                onChange={(addr) => setAddress(addr)}
                placeholder="Start typing an address..."
                data-testid="input-acquisition-address"
              />
            </div>
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setStep("source")} data-testid="button-back-to-source-2">
                ← Back
              </Button>
              <Button
                onClick={() => setStep("details")}
                disabled={!address.trim()}
                data-testid="button-continue-to-details"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === "details" && (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg border">
              <p className="text-xs text-gray-500">Property</p>
              <p className="text-sm font-medium text-gray-900">{address}</p>
              {isFromUnderwriting && <Badge className="mt-1 bg-amber-100 text-amber-700 hover:bg-amber-100 text-xs">From Underwriting</Badge>}
            </div>
            <div>
              <Label htmlFor="acq-status">Pipeline Stage</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as AcquisitionStatus)}>
                <SelectTrigger data-testid="select-acquisition-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACQUISITION_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="acq-offer-price">Offer Amount</Label>
              <Input
                id="acq-offer-price"
                value={offerPrice}
                onChange={(e) => setOfferPrice(e.target.value)}
                placeholder="250000"
                data-testid="input-acquisition-offer-price"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep(isFromUnderwriting ? "pick-property" : "new-address")} data-testid="button-back-to-prev">
                ← Back
              </Button>
              <Button
                onClick={handleCreate}
                disabled={isPending}
                data-testid="button-create-acquisition"
              >
                {isPending ? "Creating..." : "Create Acquisition"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "upload-contract" && (
          <div className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleContractFileChange}
              className="hidden"
              data-testid="input-contract-file"
            />
            {extracting ? (
              <ContractExtractionProgress active={extracting} fileName={pdfFileName} variant="dialog" />
            ) : (
              <div className="text-center py-6">
                <div className="space-y-3">
                  <Upload className="h-10 w-10 mx-auto text-purple-400" />
                  <p className="text-sm font-medium text-gray-700">Upload your executed purchase contract</p>
                  <p className="text-xs text-gray-500">PDF format, max 15MB. AI will extract property details, dates, and contacts (usually ~15 seconds).</p>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2"
                    data-testid="button-select-contract-file"
                  >
                    <Upload className="h-4 w-4 mr-2" /> Select PDF
                  </Button>
                </div>
              </div>
            )}
            <Button variant="ghost" size="sm" onClick={() => setStep("source")} disabled={extracting} data-testid="button-back-from-upload">
              ← Back
            </Button>
          </div>
        )}

        {step === "review-contract" && extracted && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 rounded-lg px-3 py-2">
              <Sparkles className="h-4 w-4 shrink-0" />
              <span>Review the extracted fields. Check or uncheck to include in your new acquisition. You can edit any value.</span>
            </div>

            <div className="p-2 bg-gray-50 rounded-lg border">
              <p className="text-xs text-gray-500 font-medium mb-1">Property Address</p>
              {Object.keys(ADDRESS_LABELS).map((k) => {
                const val = extracted[k];
                const hasValue = val != null && val !== "";
                return (
                  <div key={k} className="flex items-center gap-2 py-1" data-testid={`contract-address-${k}`}>
                    <Checkbox
                      checked={accepted[k] || false}
                      onCheckedChange={(v) => setAccepted((a) => ({ ...a, [k]: !!v }))}
                      disabled={!hasValue}
                      data-testid={`checkbox-${k}`}
                    />
                    <span className="text-xs text-gray-500 w-20 shrink-0">{ADDRESS_LABELS[k]}</span>
                    {accepted[k] ? (
                      <Input
                        value={editedValues[k] || ""}
                        onChange={(e) => setEditedValues((ev) => ({ ...ev, [k]: e.target.value }))}
                        className="text-sm h-7 flex-1"
                        data-testid={`input-${k}`}
                      />
                    ) : (
                      <span className="text-sm text-gray-700">{hasValue ? val : <span className="italic text-gray-400">Not found</span>}</span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="space-y-1">
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 pb-1 border-b">
                <span>Accept</span>
                <span>Field / Extracted Value</span>
              </div>
              {CONTRACT_FIELD_KEYS.map((k) => {
                const extractedVal = extracted[k];
                const hasValue = extractedVal != null && extractedVal !== "";
                const displayExtracted = hasValue
                  ? DATE_FIELDS_SET.has(k)
                    ? extractedVal!
                    : PHONE_FIELDS_SET.has(k)
                    ? formatPhone(extractedVal!)
                    : CURRENCY_FIELDS_SET.has(k)
                    ? formatCurrency(extractedVal!)
                    : extractedVal!
                  : null;

                return (
                  <div key={k} className={`grid grid-cols-[auto_1fr] gap-x-3 items-start p-2 rounded ${accepted[k] ? "bg-blue-50" : "opacity-60"}`} data-testid={`contract-field-${k}`}>
                    <div className="pt-1">
                      <Checkbox
                        checked={accepted[k] || false}
                        onCheckedChange={(v) => setAccepted((a) => ({ ...a, [k]: !!v }))}
                        disabled={!hasValue}
                        data-testid={`checkbox-${k}`}
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-700 mb-0.5">{CONTRACT_FIELD_LABELS[k]}</p>
                      {hasValue ? (
                        accepted[k] ? (
                          PHONE_FIELDS_SET.has(k) ? (
                            <PhoneInput
                              value={editedValues[k] || ""}
                              onChange={(v) => setEditedValues((ev) => ({ ...ev, [k]: v }))}
                              testId={`input-${k}`}
                            />
                          ) : (
                            <Input
                              type={DATE_FIELDS_SET.has(k) ? "date" : "text"}
                              value={editedValues[k] || ""}
                              onChange={(e) => setEditedValues((ev) => ({ ...ev, [k]: e.target.value }))}
                              className="text-sm h-8"
                              data-testid={`input-${k}`}
                            />
                          )
                        ) : (
                          <p className="text-sm text-gray-600">{displayExtracted}</p>
                        )
                      ) : (
                        <p className="text-sm text-gray-400 italic">Not found</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded p-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
              <span>A new acquisition will be created with the accepted fields. The PDF will be saved as a contract document.</span>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("upload-contract")} data-testid="button-back-from-review">
                ← Back
              </Button>
              <Button
                onClick={() => createFromContractMutation.mutate()}
                disabled={createFromContractMutation.isPending || !accepted["propertyAddress"]}
                data-testid="button-create-from-contract"
              >
                {createFromContractMutation.isPending ? "Creating..." : "Create Acquisition"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KanbanView({ acquisitions, onNavigate }: { acquisitions: Acquisition[]; onNavigate: (id: number) => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const dragRef = useRef<number | null>(null);
  const [closingAcq, setClosingAcq] = useState<Acquisition | null>(null);
  const [kanbanCloseNoReno, setKanbanCloseNoReno] = useState<boolean>(false);

  const updateMutation = useMutation({
    mutationFn: async ({ id, status, noRenovationNeeded }: { id: number; status: string; noRenovationNeeded?: boolean }) => {
      return apiRequest("PATCH", `/api/app/acquisitions/${id}`, { status, ...(noRenovationNeeded !== undefined ? { noRenovationNeeded } : {}) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions"] });
    },
    onError: (error: Error) => {
      const isActiveMembersBlock = (() => {
        try {
          const match = error.message.match(/^(\d{3}):\s*([\s\S]*)$/);
          const parsed = JSON.parse(match ? match[2] : error.message);
          return parsed?.code === "ACTIVE_MEMBERS_PRESENT";
        } catch { return false; }
      })() || error.message.includes("members assigned") || error.message.includes("active members");
      if (isActiveMembersBlock) {
        toast({
          title: "Can't move this property",
          description: "Cannot change status while active members are assigned to rooms. Remove or reassign all members first.",
          variant: "destructive",
        });
        queryClient.invalidateQueries({ queryKey: ["/api/app/acquisitions"] });
      } else {
        toast({ title: "Failed to update status", variant: "destructive" });
      }
    },
  });

  const handleDrop = (status: AcquisitionStatus) => {
    if (dragRef.current !== null) {
      if (status === "closed") {
        const acq = acquisitions.find((a) => a.id === dragRef.current) || null;
        setClosingAcq(acq);
        setKanbanCloseNoReno(acq?.noRenovationNeeded ?? false);
        dragRef.current = null;
      } else {
        updateMutation.mutate({ id: dragRef.current, status });
        dragRef.current = null;
      }
    }
  };

  const activeStatuses = ACQUISITION_STATUSES.filter((s) => s !== "closed");

  return (
    <>
    <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
      {activeStatuses.map((colStatus) => {
        const colAcqs = acquisitions.filter((a) => a.status === colStatus);
        return (
          <div
            key={colStatus}
            className="flex-shrink-0 w-60 bg-gray-50 rounded-lg p-2"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(colStatus)}
            data-testid={`kanban-column-${colStatus}`}
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-xs font-semibold text-gray-600">{STATUS_LABELS[colStatus]}</span>
              <span className="text-xs bg-white border rounded-full w-5 h-5 flex items-center justify-center text-gray-500">{colAcqs.length}</span>
            </div>
            <div className="space-y-2">
              {colAcqs.map((acq) => (
                <div
                  key={acq.id}
                  draggable
                  onDragStart={() => { dragRef.current = acq.id; }}
                  onClick={() => onNavigate(acq.id)}
                  className="bg-white border rounded-md p-2.5 cursor-pointer hover:shadow-sm transition-shadow"
                  data-testid={`kanban-card-${acq.id}`}
                >
                  <p className="text-xs font-medium text-gray-900 line-clamp-2 mb-1">{acq.address}</p>
                  {acq.offerPrice && (
                    <p className="text-xs text-gray-500">{formatCurrency(acq.offerPrice)}</p>
                  )}
                  {(() => {
                    const nd = getNextDeadline(acq);
                    if (!nd) return null;
                    const days = differenceInDays(new Date(nd.date), new Date());
                    return (
                      <p className={`text-xs mt-1 ${days <= 3 ? "text-red-600 font-medium" : days <= 7 ? "text-orange-600" : "text-gray-400"}`}>
                        {nd.label}: {days}d
                      </p>
                    );
                  })()}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div
        className="flex-shrink-0 w-60 bg-green-50 rounded-lg p-2"
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => handleDrop("closed")}
        data-testid="kanban-column-closed"
      >
        <div className="flex items-center justify-between mb-2 px-1">
          <span className="text-xs font-semibold text-green-700">Closed</span>
          <span className="text-xs bg-white border rounded-full w-5 h-5 flex items-center justify-center text-gray-500">
            {acquisitions.filter((a) => a.status === "closed").length}
          </span>
        </div>
        <div className="space-y-2">
          {acquisitions.filter((a) => a.status === "closed").map((acq) => (
            <div
              key={acq.id}
              draggable
              onDragStart={() => { dragRef.current = acq.id; }}
              onClick={() => onNavigate(acq.id)}
              className="bg-white border rounded-md p-2.5 cursor-pointer hover:shadow-sm transition-shadow opacity-75"
              data-testid={`kanban-card-${acq.id}`}
            >
              <p className="text-xs font-medium text-gray-900 line-clamp-2 mb-1">{acq.address}</p>
              {acq.offerPrice && <p className="text-xs text-gray-500">{formatCurrency(acq.offerPrice)}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>

      <Dialog open={!!closingAcq} onOpenChange={(o) => { if (!o) setClosingAcq(null); }}>
        <DialogContent className="max-w-sm" data-testid="dialog-kanban-close-confirm">
          <DialogHeader>
            <DialogTitle>Close Acquisition</DialogTitle>
            <DialogDescription>
              Where should the linked property be moved in your workflow?
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Button
              variant={!kanbanCloseNoReno ? "default" : "outline"}
              className={`flex flex-col h-auto py-4 border-2 ${!kanbanCloseNoReno ? "border-orange-600 bg-orange-600 hover:bg-orange-700 text-white" : "hover:border-orange-500 hover:bg-orange-50"}`}
              onClick={() => setKanbanCloseNoReno(false)}
              data-testid="button-kanban-close-select-renovations"
            >
              <span className="font-semibold">Renovations</span>
              <span className={`text-xs mt-1 ${!kanbanCloseNoReno ? "text-orange-100" : "text-gray-500"}`}>Property needs work</span>
            </Button>
            <Button
              variant={kanbanCloseNoReno ? "default" : "outline"}
              className={`flex flex-col h-auto py-4 border-2 ${kanbanCloseNoReno ? "border-green-600 bg-green-600 hover:bg-green-700 text-white" : "hover:border-green-500 hover:bg-green-50"}`}
              onClick={() => setKanbanCloseNoReno(true)}
              data-testid="button-kanban-close-select-active"
            >
              <span className="font-semibold">Active</span>
              <span className={`text-xs mt-1 ${kanbanCloseNoReno ? "text-green-100" : "text-gray-500"}`}>No renovation needed</span>
            </Button>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setClosingAcq(null)} data-testid="button-kanban-close-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (closingAcq) updateMutation.mutate({ id: closingAcq.id, status: "closed", noRenovationNeeded: kanbanCloseNoReno });
                setClosingAcq(null);
              }}
              disabled={updateMutation.isPending}
              data-testid="button-kanban-close-confirm"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AppAcquisitions() {
  usePageTitle("Acquisitions");
  const [, navigate] = useLocation();
  const [view, persistView] = useLocalStorageState<"list" | "kanban">(
    "pp:acquisitions-view",
    "list",
    (stored) => (stored === "kanban" ? "kanban" : "list"),
  );
  const [search, setSearch] = useState("");
  const [filterStatus, persistFilterStatus] = useLocalStorageState(
    "pp:acquisitions-filter-status",
    "all",
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [sortField, setSortField] = useLocalStorageState<"address" | "status" | "offerPrice" | "deadline" | "daysInStatus">(
    "pp:acquisitions-sort-field",
    "address",
    (stored) =>
      ACQUISITION_SORT_FIELDS.includes(stored as AcquisitionSortField)
        ? (stored as AcquisitionSortField)
        : "address",
  );
  const [sortDir, setSortDir] = useLocalStorageState<"asc" | "desc">(
    "pp:acquisitions-sort-dir",
    "asc",
    (stored) => (stored === "desc" ? "desc" : "asc"),
  );

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const { data: acquisitions = [], isLoading } = useQuery<Acquisition[]>({
    queryKey: ["/api/app/acquisitions"],
  });
  const journeyStage = useJourneyStage();

  const filtered = acquisitions.filter((a) => {
    const matchesSearch = !search || String(a.address ?? "").toLowerCase().includes(search.toLowerCase());
    const matchesStatus = filterStatus === "all" || a.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    switch (sortField) {
      case "address":
        return dir * a.address.localeCompare(b.address);
      case "status": {
        const ai = ACQUISITION_STATUSES.indexOf(a.status as AcquisitionStatus);
        const bi = ACQUISITION_STATUSES.indexOf(b.status as AcquisitionStatus);
        return dir * (ai - bi);
      }
      case "offerPrice": {
        const ap = parseFloat((a.offerPrice || "0").replace(/[^0-9.]/g, "")) || 0;
        const bp = parseFloat((b.offerPrice || "0").replace(/[^0-9.]/g, "")) || 0;
        return dir * (ap - bp);
      }
      case "deadline": {
        const ad = getNextDeadline(a);
        const bd = getNextDeadline(b);
        const at = ad ? new Date(ad.date).getTime() : Infinity;
        const bt = bd ? new Date(bd.date).getTime() : Infinity;
        return dir * (at - bt);
      }
      case "daysInStatus":
        return dir * (getDaysInStatus(a) - getDaysInStatus(b));
      default:
        return 0;
    }
  });

  const totalPipelineValue = acquisitions
    .filter((a) => a.status !== "closed")
    .reduce((sum, a) => {
      if (!a.offerPrice) return sum;
      const n = parseFloat(a.offerPrice.replace(/[^0-9.]/g, ""));
      return isNaN(n) ? sum : sum + n;
    }, 0);

  const statusCounts = ACQUISITION_STATUSES.reduce((acc, s) => {
    acc[s] = acquisitions.filter((a) => a.status === s).length;
    return acc;
  }, {} as Record<AcquisitionStatus, number>);

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="page-acquisitions">
        <ViewOnlyBanner area="properties" />
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-tour="acquisitions-heading">Acquisitions</h1>
            <p className="text-sm text-gray-500 mt-1">Track properties from offer to closing</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setCreateOpen(true)} data-testid="button-new-acquisition" data-tour="acquisitions-add">
              <Plus className="h-4 w-4 mr-2" /> New Acquisition
            </Button>
            <ExplainerVideoButton pageKey="acquisitions" />
            <GuideButton pageKey="acquisitions" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="acquisitions" />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Card data-testid="stat-active-deals">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Home className="h-4 w-4 text-blue-500" />
                <span className="text-xs text-gray-500">Active Deals</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{acquisitions.filter(a => a.status !== "closed").length}</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-pipeline-value">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-green-500" />
                <span className="text-xs text-gray-500">Pipeline Value</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {totalPipelineValue > 0 ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(totalPipelineValue) : "—"}
              </p>
            </CardContent>
          </Card>
          <Card data-testid="stat-under-contract">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-purple-500" />
                <span className="text-xs text-gray-500">Under Contract</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{statusCounts["under_contract"] + statusCounts["due_diligence"] + statusCounts["appraisal"] + statusCounts["clear_to_close"] + statusCounts["closing"]}</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-closed">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4 text-gray-500" />
                <span className="text-xs text-gray-500">Closed</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{statusCounts["closed"]}</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search by address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="input-search-acquisitions"
            />
          </div>
          <Select value={filterStatus} onValueChange={persistFilterStatus}>
            <SelectTrigger className="w-44" data-testid="select-filter-status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {ACQUISITION_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex border rounded-md overflow-hidden">
            <button
              className={`px-3 py-2 text-sm flex items-center gap-1.5 ${view === "list" ? "bg-gray-100 font-medium" : "hover:bg-gray-50 text-gray-600"}`}
              onClick={() => persistView("list")}
              data-testid="button-list-view"
            >
              <LayoutList className="h-4 w-4" /> List
            </button>
            <button
              className={`px-3 py-2 text-sm flex items-center gap-1.5 border-l ${view === "kanban" ? "bg-gray-100 font-medium" : "hover:bg-gray-50 text-gray-600"}`}
              onClick={() => persistView("kanban")}
              data-testid="button-kanban-view"
            >
              <Columns className="h-4 w-4" /> Board
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : view === "kanban" ? (
          <KanbanView acquisitions={sorted} onNavigate={(id) => navigate(`/app/acquisitions/${id}`)} />
        ) : (
          <div data-testid="acquisitions-list" data-tour="acquisitions-list">
            {filtered.length === 0 ? (
              acquisitions.length === 0 ? (
                <JourneyEmptyState
                  page="acquisitions"
                  stage={journeyStage}
                  icon={<Home className="h-10 w-10 text-gray-300" />}
                  primaryAction={{ onClick: () => setCreateOpen(true) }}
                />
              ) : (
                <div className="text-center py-16 text-gray-400" data-testid="empty-acquisitions-filtered">
                  <Home className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium text-gray-500">No acquisitions match your filters</p>
                  <p className="text-sm mt-1">Try clearing your search or status filter.</p>
                </div>
              )
            ) : (
              <div className="bg-white border rounded-lg overflow-x-auto">
                <table className="w-full text-sm min-w-[680px]">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none" onClick={() => toggleSort("address")} data-testid="sort-address"><span className="flex items-center">Address<SortIcon field="address" /></span></th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none" onClick={() => toggleSort("status")} data-testid="sort-status"><span className="flex items-center">Status<SortIcon field="status" /></span></th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none" onClick={() => toggleSort("offerPrice")} data-testid="sort-offer-price"><span className="flex items-center">Offer Price<SortIcon field="offerPrice" /></span></th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none" onClick={() => toggleSort("deadline")} data-testid="sort-deadline"><span className="flex items-center">Next Deadline<SortIcon field="deadline" /></span></th>
                      <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none" onClick={() => toggleSort("daysInStatus")} data-testid="sort-days-in-status"><span className="flex items-center">Days in Status<SortIcon field="daysInStatus" /></span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((acq) => {
                      const nd = getNextDeadline(acq);
                      const daysInStatus = getDaysInStatus(acq);
                      return (
                        <tr
                          key={acq.id}
                          className="border-b last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                          {...rowNavHandlers(`/app/acquisitions/${acq.id}`, navigate)}
                          data-testid={`acquisition-row-${acq.id}`}
                        >
                          <td className="px-4 py-3 font-medium text-gray-900">
                            <Link href={`/app/acquisitions/${acq.id}`} className="block -mx-4 -my-3 px-4 py-3" onClick={(e) => e.stopPropagation()}>{acq.address}</Link>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={`${STATUS_COLORS[acq.status as AcquisitionStatus]} border-0 text-xs`}>
                              {STATUS_LABELS[acq.status as AcquisitionStatus] || acq.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-gray-700">{formatCurrency(acq.offerPrice)}</td>
                          <td className="px-4 py-3">
                            {nd ? (
                              <div>
                                <span className="text-gray-600">{nd.label}</span>
                                <span className={`ml-2 text-xs font-medium ${
                                  differenceInDays(new Date(nd.date), new Date()) <= 3 ? "text-red-600" :
                                  differenceInDays(new Date(nd.date), new Date()) <= 7 ? "text-orange-600" : "text-gray-400"
                                }`}>
                                  {format(new Date(nd.date), "MMM d")}
                                </span>
                              </div>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{daysInStatus}d</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      <CreateAcquisitionDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </AppLayout>
  );
}
